/**
 * @fileoverview Renders the verification code entry as well as handle network calls
 * and side effects tied to verification.
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */

import {ExternalAccountsContext}
  from '/js/yext/externalaccounts/shared/listingslaunch/externalaccountscontext';
import {useDebounce} from '/src/com/yext/listingslaunchstorm/js/common/hooks/usedebounce';
import {useFetch} from '/src/com/yext/listingslaunchstorm/js/common/hooks/usefetch';
import {VerificationVideoTips} from '/src/com/yext/listingslaunchstorm/js/components/verification/verificationvideotips';
import {ListingsLaunchContext} from '/src/com/yext/listingslaunchstorm/js/contexts/listingslaunchcontextprovider';
import {RequestVerificationFromPublisherRequest} from '/src/com/yext/listingslaunchstorm/js/data/requestverificationfrompublisherrequest';
import {SubmitVerificationToPublisherRequest} from '/src/com/yext/listingslaunchstorm/js/data/submitverificationtopublisherrequest';
import {VerificationMethod} from '/src/com/yext/listingslaunchstorm/js/data/verificationmethod';
import {VerifiedListing} from '/src/com/yext/listingslaunchstorm/js/data/verifiedlisting';
import requestVerificationFromPublisher from '/src/com/yext/listingslaunchstorm/js/datasource/requestverificationfrompublisher';
import submitVerificationToPublisher from '/src/com/yext/listingslaunchstorm/js/datasource/submitverificationtopublisher';
import {Button} from '/ui/components/button/button';
import {Input} from '/ui/components/input/input';
import {IconTooltip} from '/ui/components/tooltip/tooltip';
import {useIsSmallViewport} from '/ui/lib/responsivity';

import * as styles from '/src/com/yext/listingslaunchstorm/js/components/styles/verifyentity.module.scss';
import CheckedSvg from '/src/com/yext/listingslaunchstorm/public/icons/green-checkmark.svg';

const CODE_MIN_LENGTH = 5;
const CODE_MAX_LENGTH = 6;

/**
 * @param {{
 *  entityId: !number,
 *  externalId: !string,
 *  groupId: !string,
 *  verificationMethod: !VerificationMethod,
 *  verificationDestination: !string,
 *  onCancel: function(): void,
 *  canSwitchDestination: boolean,
 *  onSwitchDestinationForMethod: function(VerificationMethod): void,
 * }} props
 * @return {React.ReactNode}
 */
const VerificationCodeEntry = ({
  entityId,
  externalId,
  groupId,
  verificationMethod,
  verificationDestination,
  onCancel,
  canSwitchDestination,
  onSwitchDestinationForMethod,
}) => {
  const {setVerifiedListings} = React.useContext(ListingsLaunchContext);
  const {publisherIdToExternalAccount} = React.useContext(ExternalAccountsContext);
  const linkedAccount = publisherIdToExternalAccount.get(715);
  const isSmallViewport = useIsSmallViewport();

  const [enteredVerificationCode, setEnteredVerificationCode] = React.useState('');
  const [verificationAttemptMade, setVerificationAttemptMade] = React.useState(false);
  const [hasResentCode, setHasResentCode] = React.useState(true);

  const {status, data, refetch} = useFetch(submitVerificationToPublisher);
  const validate = React.useCallback(code => {
    if (code.length >= CODE_MIN_LENGTH && code.length <= CODE_MAX_LENGTH) {
      setVerificationAttemptMade(true);
      refetch(new SubmitVerificationToPublisherRequest({
        businessId: yext.businessId,
        entityId,
        externalId,
        linkedAccountId: linkedAccount.id,
        groupId: groupId,
        code,
      }));
    }
  }, [refetch, entityId, externalId, linkedAccount.id, groupId]);

  const {debouncedCallback} = useDebounce(validate, 1000);

  const isLoading = !verificationAttemptMade || status === 'loading';

  const isSuccess = status === 'success' && !data.hasError;
  const isValid = !isLoading && isSuccess;
  const isInvalid = !isLoading && !isSuccess;

  React.useEffect(() => {
    if (!verificationAttemptMade) {
      debouncedCallback(enteredVerificationCode);
    } else if (isValid) {
      const newVerification = new VerifiedListing({
        entityId: entityId,
        method: verificationMethod,
      });

      const getOtherVerifiedListings = currentVerifiedListings => (
        currentVerifiedListings.filter(verification => verification.entityId !== entityId)
      );
      setVerifiedListings(currentVerifiedListings => [
        ...getOtherVerifiedListings(currentVerifiedListings),
        newVerification,
      ]);
    }
  }, [
    verificationAttemptMade,
    debouncedCallback,
    enteredVerificationCode,
    isValid,
    entityId,
    verificationMethod,
    setVerifiedListings,
  ]);

  // There is a 30 second limit on resending verification codes.
  React.useEffect(() => {
    const timer = setInterval(() => {
      setHasResentCode(false);
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  const isVideo = verificationMethod === VerificationMethod.VIDEO;
  const getMessage = () => {
    if (verificationDestination) {
      return yext.msg('Enter the code sent to {0}. If a code is not delivered, you can resend a new code after the 30 second cooldown period.', verificationDestination);
    } else if (isVideo) {
      return yext.msg('If a new tab did not open, you can launch the video verification process again. Otherwise, follow along with the instructions below.');
    } else {
      return yext.msg('Enter the code you received. If you need to request another code then choose from one of the verification options available.');
    }
  };

  const isValidEmail = verificationDestination && verificationMethod === VerificationMethod.EMAIL;
  const switchEmailButton = (
    <Button
      context="default"
      size={isSmallViewport ? 'medium' : 'small'}
      padding={isSmallViewport ? '0px' : '8px'}
      fullWidth={isSmallViewport}
      onClick={() => onSwitchDestinationForMethod(verificationMethod)}
    >
      {yext.msg('Switch Email')}
    </Button>
  );

  return (
    <div className={styles.verificationCodeContainer}>
      <p>{getMessage()}</p>
      <div className={styles.verificationDetails}>
        {!isVideo && (
          <div className={styles.verificationFormEntry}>
            <Input
              id={'text-message-code-verification'}
              label={
                isValid === true && (
                  <div className={styles.successLabel}>
                    <CheckedSvg />
                    <span>{yext.msg('Success!')}</span>
                  </div>
                )}
              errorMessage={isInvalid ? yext.msg('That code is incorrect or incomplete. Try again or resend a new code.') : yext.msg('')}
              valid={!isInvalid}
              placeholder={yext.msg('Verification Code (e.g., 123456)')}
              maxLength={CODE_MAX_LENGTH}
              onChange={currentEnteredCode => {
                setEnteredVerificationCode(currentEnteredCode);
                setVerificationAttemptMade(false);
              }}
            />
          </div>
        )}
        <div className={styles.codeCtas}>
          {isSmallViewport && (
            <Button
              fullWidth
              context="default"
              onClick={onCancel}
            >
              {yext.msg('Switch Method')}
            </Button>
          )}
          {isVideo && (
            <Button
              fullWidth={isSmallViewport}
              context="default"
              onClick={() => {}}
            >
              {yext.msg('Launch Again')}
            </Button>
          )}
          {(isSmallViewport && isValidEmail && canSwitchDestination) && switchEmailButton}
          {verificationDestination && (
            <IconTooltip
              element={
                <Button
                  pendoId="listings-launch-flow-verifying-resend-code"
                  context="default"
                  size={isSmallViewport ? 'medium' : 'small'}
                  padding={isSmallViewport ? '0px' : '8px'}
                  fullWidth={isSmallViewport}
                  disabled={hasResentCode}
                  onClick={() => {
                    requestVerificationFromPublisher(
                      constructRequestVerificationFromPublisherRequest(entityId, externalId, linkedAccount, groupId, verificationMethod, verificationDestination),
                      new AbortController().signal,
                    );
                    setHasResentCode(true);
                  }}
                >
                  {yext.msg('Resend Code')}
                </Button>
              }
              visible={hasResentCode ? undefined : false} // undefined results in hover-displaying behavior, so if the verification code was resent, set visible to undefined.
              tooltipText={yext.msg('There is a 30 second cooldown on codes. Wait and try again.')}
            />
          )}
          {(!isSmallViewport && isValidEmail && canSwitchDestination) && switchEmailButton}
        </div>
      </div>
      {isVideo && (<VerificationVideoTips />)}
    </div>
  );
};

/**
 * Constructs a RequestVerificationFromPublisherRequest.
 * @param {number} entityId
 * @param {string} externalId
 * @param {number} linkedAccount
 * @param {string} groupId
 * @param {string} verificationMethod
 * @param {string} verificationDestination
 * @return {!RequestVerificationFromPublisherRequest}
 */
const constructRequestVerificationFromPublisherRequest = (entityId, externalId, linkedAccount, groupId, verificationMethod, verificationDestination) => {
  return new RequestVerificationFromPublisherRequest({
    entityId: entityId,
    externalId: externalId,
    linkedAccountId: linkedAccount.id,
    groupId: groupId,
    method: verificationMethod,
    address:
      verificationMethod === VerificationMethod.POSTCARD
        ? verificationDestination
        : undefined,
    phone:
      verificationMethod === VerificationMethod.PHONE_CALL
      || verificationMethod === VerificationMethod.SMS
        ? verificationDestination
        : undefined,
    email:
      verificationMethod === VerificationMethod.EMAIL
        ? verificationDestination
        : undefined,
  });
};

export default VerificationCodeEntry;
